/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React from 'react';
import { Text, TouchableOpacity, View, type StyleProp, type ViewStyle, type TextStyle } from 'react-native';
import { styles } from '../posShared.styles';
import { colors } from '../../../shared/theme/colors';
import { MoneySummaryRow } from '../checkout/MoneySummaryRow';
import { formatPosMoney } from '../../application/posMoneyFormat';

export type PosCartFooterProps = {
  total: number;
  savingsLabel: string | null;
  promoSavingsLines?: string[];
  isSaving: boolean;
  canEdit: boolean;
  onOpenCheckout: () => void;
  onSendCourseToKitchen?: () => void;
  canSendCourseToKitchen?: boolean;
  onPrintPreCuenta?: () => void;
  onSharePreCuenta?: () => void;
  canPrintPreCuenta?: boolean;
  /** Mesa abierta: label Imprimir cuenta; carrito libre: Imprimir ticket */
  printTicketIsPreCuenta?: boolean;
  /** Mostrador QSR: Resumen del pedido (G6). */
  printTicketCounterSummary?: boolean;
  onOpenSplitBill?: () => void;
  canSplitBill?: boolean;
  useMobileCartCompact: boolean;
  isCompactCart: boolean;
  inSheet?: boolean;
  chargeBlocked?: boolean;
  /** P1-07: selección de promo inválida — no abrir checkout. */
  promoStaleBlocked?: boolean;
};

function OutlineAction({
  label,
  busyLabel,
  isSaving,
  canEdit,
  onPress,
  accessibilityLabel,
  buttonStyle,
  textStyle,
  testID,
}: {
  label: string;
  busyLabel: string;
  isSaving: boolean;
  canEdit: boolean;
  onPress?: () => void;
  accessibilityLabel: string;
  buttonStyle: StyleProp<ViewStyle>;
  textStyle: StyleProp<TextStyle>;
  testID?: string;
}) {
  return (
    <TouchableOpacity
      style={[buttonStyle, isSaving && { opacity: 0.6 }]}
      disabled={isSaving || !canEdit}
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      testID={testID}
    >
      <Text style={textStyle}>{isSaving ? busyLabel : label}</Text>
    </TouchableOpacity>
  );
}

export type PosCartTableActionsProps = {
  kind: 'sheet' | 'scroll';
  isSaving: boolean;
  canEdit: boolean;
  onOpenSplitBill?: () => void;
  canSplitBill?: boolean;
  onPrintPreCuenta?: () => void;
  onSharePreCuenta?: () => void;
  canPrintPreCuenta?: boolean;
  /** Mesa abierta: label Imprimir cuenta; carrito libre: Imprimir ticket */
  printTicketIsPreCuenta?: boolean;
  /** Mostrador QSR: Resumen del pedido (G6). */
  printTicketCounterSummary?: boolean;
  onSendCourseToKitchen?: () => void;
  canSendCourseToKitchen?: boolean;
};

/** Acciones secundarias del ticket (dividir, imprimir, compartir, cocina). */
export function PosCartTableActions({
  kind,
  isSaving,
  canEdit,
  onOpenSplitBill,
  canSplitBill = false,
  onPrintPreCuenta,
  onSharePreCuenta,
  canPrintPreCuenta = false,
  printTicketIsPreCuenta = false,
  printTicketCounterSummary = false,
  onSendCourseToKitchen,
  canSendCourseToKitchen = false,
}: PosCartTableActionsProps) {
  const printTicketLabel = printTicketIsPreCuenta
    ? 'Imprimir cuenta'
    : printTicketCounterSummary
      ? 'Resumen del pedido'
      : 'Imprimir ticket';
  const printTicketA11y = printTicketIsPreCuenta
    ? 'Imprimir cuenta'
    : printTicketCounterSummary
      ? 'Imprimir resumen del pedido'
      : 'Imprimir ticket';
  const shareTicketLabel = printTicketIsPreCuenta
    ? 'Compartir cuenta'
    : printTicketCounterSummary
      ? 'Compartir resumen'
      : 'Compartir ticket';
  const outlineBase = {
    backgroundColor: colors.surface,
    borderColor: colors.accent,
    borderWidth: 1,
  } as const;
  const outlineText = { color: colors.accent } as const;

  if (!canSplitBill && !canPrintPreCuenta && !canSendCourseToKitchen) return null;

  return (
    <View style={kind === 'scroll' ? { gap: 8, marginTop: 8, marginBottom: 4 } : undefined}>
      {canSplitBill ? (
        <OutlineAction
          label="Dividir cuenta"
          busyLabel="…"
          isSaving={isSaving}
          canEdit={canEdit}
          onPress={onOpenSplitBill}
          accessibilityLabel="Dividir cuenta"
          buttonStyle={[
            styles.posPrimaryCta,
            styles.posPrimaryCtaFull,
            kind === 'sheet' && styles.posPrimaryCtaSheet,
            outlineBase,
          ]}
          textStyle={[
            styles.posPrimaryCtaText,
            kind === 'sheet' && styles.posPrimaryCtaTextSheet,
            outlineText,
          ]}
        />
      ) : null}
      {canPrintPreCuenta ? (
        <OutlineAction
          label={printTicketLabel}
          busyLabel="Imprimiendo…"
          isSaving={isSaving}
          canEdit={canEdit}
          onPress={onPrintPreCuenta}
          accessibilityLabel={printTicketA11y}
          testID="pos-print-ticket"
          buttonStyle={[
            styles.posPrimaryCta,
            styles.posPrimaryCtaFull,
            kind === 'sheet' && styles.posPrimaryCtaSheet,
            outlineBase,
          ]}
          textStyle={[
            styles.posPrimaryCtaText,
            kind === 'sheet' && styles.posPrimaryCtaTextSheet,
            outlineText,
          ]}
        />
      ) : null}
      {canPrintPreCuenta && onSharePreCuenta ? (
        <OutlineAction
          label={shareTicketLabel}
          busyLabel="Compartiendo…"
          isSaving={isSaving}
          canEdit={canEdit}
          onPress={onSharePreCuenta}
          accessibilityLabel={shareTicketLabel}
          testID="pos-share-ticket"
          buttonStyle={[
            styles.posPrimaryCta,
            styles.posPrimaryCtaFull,
            kind === 'sheet' && styles.posPrimaryCtaSheet,
            outlineBase,
          ]}
          textStyle={[
            styles.posPrimaryCtaText,
            kind === 'sheet' && styles.posPrimaryCtaTextSheet,
            outlineText,
          ]}
        />
      ) : null}
      {canSendCourseToKitchen ? (
        <OutlineAction
          label="Mandar a cocina"
          busyLabel="Mandando…"
          isSaving={isSaving}
          canEdit={canEdit}
          onPress={onSendCourseToKitchen}
          accessibilityLabel="Mandar a cocina"
          buttonStyle={[
            styles.posPrimaryCta,
            styles.posPrimaryCtaFull,
            kind === 'sheet' && styles.posPrimaryCtaSheet,
            outlineBase,
          ]}
          textStyle={[
            styles.posPrimaryCtaText,
            kind === 'sheet' && styles.posPrimaryCtaTextSheet,
            outlineText,
          ]}
        />
      ) : null}
    </View>
  );
}

function CartPromoSavingsRows({
  savingsLabel,
  promoSavingsLines,
  textStyle,
}: {
  savingsLabel: string | null;
  promoSavingsLines: string[];
  textStyle: StyleProp<TextStyle>;
}) {
  const lines =
    promoSavingsLines.length > 0
      ? promoSavingsLines
      : savingsLabel
        ? [`−${savingsLabel}`]
        : [];
  if (lines.length === 0) return null;
  return (
    <>
      {lines.map((line) => (
        <Text key={line} style={textStyle} numberOfLines={2}>
          {line}
        </Text>
      ))}
    </>
  );
}

export const PosCartFooter = ({
  total,
  savingsLabel,
  promoSavingsLines = [],
  isSaving,
  canEdit,
  onOpenCheckout,
  onSendCourseToKitchen,
  canSendCourseToKitchen = false,
  onPrintPreCuenta,
  onSharePreCuenta,
  canPrintPreCuenta = false,
  printTicketIsPreCuenta = false,
  printTicketCounterSummary = false,
  onOpenSplitBill,
  canSplitBill = false,
  useMobileCartCompact,
  isCompactCart,
  inSheet = false,
  chargeBlocked = false,
  promoStaleBlocked = false,
}: PosCartFooterProps) => {
  const compactFooter = isCompactCart && !inSheet;
  const totalText = formatPosMoney(total);
  const ctaLabel = isSaving
    ? 'Cobrando…'
    : chargeBlocked
      ? 'Sin conexión'
      : promoStaleBlocked
        ? 'Promo vencida'
        : 'Cobrar';
  const chargeDisabled = isSaving || !canEdit || chargeBlocked || promoStaleBlocked;

  if (inSheet) {
    return (
      <View style={[styles.cartCheckoutFooter, styles.cartCheckoutFooterSticky, styles.cartCheckoutFooterSheet]}>
        <PosCartTableActions
          kind="sheet"
          isSaving={isSaving}
          canEdit={canEdit}
          onOpenSplitBill={onOpenSplitBill}
          canSplitBill={canSplitBill}
          onPrintPreCuenta={onPrintPreCuenta}
          onSharePreCuenta={onSharePreCuenta}
          canPrintPreCuenta={canPrintPreCuenta}
          printTicketIsPreCuenta={printTicketIsPreCuenta}
          printTicketCounterSummary={printTicketCounterSummary}
          onSendCourseToKitchen={onSendCourseToKitchen}
          canSendCourseToKitchen={canSendCourseToKitchen}
        />
        <View style={[styles.cartCheckoutNest, styles.cartCheckoutNestSheet]}>
          {promoSavingsLines.length > 1 ? (
            <View testID="pos-cart-savings-row">
              <CartPromoSavingsRows
                savingsLabel={savingsLabel}
                promoSavingsLines={promoSavingsLines}
                textStyle={styles.cartCheckoutHeroSavings}
              />
            </View>
          ) : savingsLabel ? (
            <Text style={styles.cartCheckoutHeroSavings} testID="pos-cart-savings-row">
              {`Ahorr\u00e1s ${savingsLabel}`}
            </Text>
          ) : null}
          <View style={styles.cartNestTotalRow}>
            <Text style={styles.cartNestLabel}>A cobrar</Text>
            <Text style={styles.cartNestValue}>{totalText}</Text>
          </View>
          <TouchableOpacity
            style={[
              styles.cartNestCta,
              styles.cartNestCtaSheet,
              isSaving && { opacity: 0.6 },
              chargeBlocked && { opacity: 0.6 },
            ]}
            disabled={chargeDisabled}
            onPress={onOpenCheckout}
            accessibilityRole="button"
            accessibilityLabel={ctaLabel}
            testID="pos-charge-cta"
          >
            <Text style={styles.cartNestCtaText}>{ctaLabel}</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  return (
    <View
      style={[
        styles.cartCheckoutFooter,
        styles.cartCheckoutFooterSticky,
        compactFooter && styles.cartCheckoutFooterCompact,
        useMobileCartCompact && styles.cartCheckoutFooterInline,
      ]}
    >
      {useMobileCartCompact ? (
        <>
          <View style={styles.cartCheckoutInlineTotalWrap}>
            {promoSavingsLines.length > 1 ? (
              <View style={styles.cartSavingsRow} testID="pos-cart-savings-row">
                <CartPromoSavingsRows
                  savingsLabel={null}
                  promoSavingsLines={promoSavingsLines}
                  textStyle={styles.cartSavingsText}
                />
              </View>
            ) : savingsLabel ? (
              <View style={styles.cartSavingsRow} testID="pos-cart-savings-row">
                <Text style={styles.cartSavingsText}>−{savingsLabel}</Text>
              </View>
            ) : null}
            <MoneySummaryRow label="A cobrar" value={totalText} variant="compact" />
          </View>
          <TouchableOpacity
            style={[
              styles.posPrimaryCta,
              styles.posPrimaryCtaCompact,
              styles.cartCheckoutInlineCta,
              (isSaving || chargeBlocked) && { opacity: 0.6 },
            ]}
            disabled={chargeDisabled}
            onPress={onOpenCheckout}
            accessibilityRole="button"
            accessibilityLabel={ctaLabel}
            testID="pos-charge-cta"
          >
            <Text style={[styles.posPrimaryCtaText, styles.posPrimaryCtaTextCompact]}>{ctaLabel}</Text>
          </TouchableOpacity>
        </>
      ) : (
        <View style={[styles.cartCheckoutNest, styles.cartCheckoutNestDesk, compactFooter && styles.cartCheckoutTotalsPanelCompact]}>
          {promoSavingsLines.length > 1 ? (
            <View style={styles.cartSavingsRow} testID="pos-cart-savings-row">
              <CartPromoSavingsRows
                savingsLabel={null}
                promoSavingsLines={promoSavingsLines}
                textStyle={styles.cartSavingsText}
              />
            </View>
          ) : savingsLabel ? (
            <View style={styles.cartSavingsRow} testID="pos-cart-savings-row">
              <Text style={styles.cartSavingsText}>−{savingsLabel}</Text>
            </View>
          ) : null}
          <View style={styles.cartNestTotalRow}>
            <Text style={styles.cartNestLabel}>A cobrar</Text>
            <Text style={styles.cartNestValue}>{totalText}</Text>
          </View>
          <TouchableOpacity
            style={[
              styles.cartNestCta,
              (isSaving || chargeBlocked) && { opacity: 0.6 },
            ]}
            disabled={chargeDisabled}
            onPress={onOpenCheckout}
            accessibilityRole="button"
            accessibilityLabel={ctaLabel}
            testID="pos-charge-cta"
          >
            <Text style={styles.cartNestCtaText}>{ctaLabel}</Text>
          </TouchableOpacity>
        </View>
      )}
    </View>
  );
};
